/**
 * 系统配置 · 允许上传的模型类别。
 *
 * 值为 JSON 数组，如 ["lidar_detection", "traffic_light", "prediction"]。模型上传页「模型类别」下拉只列这些，
 * 上传接口也只接受这些；只有「系统参数运维」权限的管理员能改。
 */
import { useState } from 'react';
import { App, Button, Input, Popconfirm, Space, Tag } from 'antd';
import { PlusOutlined, RollbackOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { getModelUploadNamesConfig, resetModelUploadNames, updateModelUploadNames } from '@apis/modelSync';
import { errorText } from '@/utils/modelSync';
import { uploadNamesError } from '@/utils/modelCatalog';
import { Panel, PanelError, PanelLoading, RefreshButton, SOURCE_PILL, useConfigResource, useReportDirty } from './shared';

const CARD_ID = 'sec-model-upload-names';

function parseNames(raw: string): string[] {
  const v = JSON.parse(raw);
  return Array.isArray(v) ? v.map(String) : [];
}

export default function ModelUploadNamesCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [names, setNames] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadDraft = (raw: string) => {
    setSaveError(null);
    setInput('');
    try {
      setNames(parseNames(raw));
      setLoadError(null);
    } catch (e) {
      setNames([]);
      setLoadError(`当前配置不是合法 JSON：${(e as Error).message}，可恢复默认后再编辑`);
    }
  };
  const { loading, config, applyConfig, fetchConfig } = useConfigResource({
    id: CARD_ID, title: '允许上传的模型类别', fetcher: getModelUploadNamesConfig,
    onLoaded: (cfg) => loadDraft(cfg.currentValue),
  });

  const draft = JSON.stringify(names, null, 2);
  const error = loadError ?? uploadNamesError(names);
  const dirty = config !== null && !loadError && (() => {
    try { return JSON.stringify(names) !== JSON.stringify(parseNames(config.currentValue)); } catch { return true; }
  })();
  useReportDirty(CARD_ID, dirty);

  const add = () => {
    const n = input.trim();
    if (!n || names.includes(n)) return;
    setNames((xs) => [...xs, n]);
    setInput('');
  };

  const handleSave = async () => {
    if (error || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      applyConfig(await updateModelUploadNames(draft));
      message.success('允许上传的模型类别已保存');
    } catch (e) {
      setSaveError(errorText(e, '保存失败，请重试'));
    } finally {
      setSaving(false);
    }
  };
  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetModelUploadNames());
      message.success('已恢复默认');
    } catch (e) {
      // 请求层已弹出错误提示
      console.error('恢复默认的允许上传模型类别失败', e);
    } finally {
      setResetting(false);
    }
  };

  const busy = saving || resetting || loading;

  return (
    <Panel
      id={CARD_ID} index={index} variant="cfg" kicker="MODEL" title="允许上传的模型类别" dirty={dirty}
      subtitle="模型上传页「模型类别」下拉只列这些；上传接口也只接受这些类别。"
      info={<div>名字只能包含字母、数字、下划线、连字符和中文，1~64 个字符，不能重复，至少保留一个。</div>}
      tools={<RefreshButton loading={loading} onClick={fetchConfig} />}
    >
      {loading && !config ? <PanelLoading /> : !config ? <PanelError onRetry={fetchConfig} /> : (
        <>
          <div className="sc-meta">{SOURCE_PILL[config.source]}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {names.map((n) => (
              <Tag key={n} closable={!busy} style={{ fontFamily: 'ui-monospace, monospace' }}
                onClose={(e) => { e.preventDefault(); setNames((xs) => xs.filter((x) => x !== n)); }}>{n}</Tag>
            ))}
            <Space.Compact size="small">
              <Input size="small" style={{ width: 200 }} maxLength={64} placeholder="新模型类别" disabled={busy}
                value={input} onChange={(e) => setInput(e.target.value)} onPressEnter={add} />
              <Button size="small" icon={<PlusOutlined />} disabled={busy || !input.trim()} onClick={add}>添加</Button>
            </Space.Compact>
          </div>
          <div className="sc-editor">
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirty || !!error || resetting || loading} onClick={() => void handleSave()}>保存</Button>
            <Button size="small" icon={<UndoOutlined />} title="撤销本地改动" aria-label="撤销允许上传模型类别的改动" disabled={!dirty || saving || resetting}
              onClick={() => loadDraft(config.currentValue)} />
            <Popconfirm title="恢复默认？" description="恢复为 lidar_detection、traffic_light、prediction。" okText="恢复默认" cancelText="取消" onConfirm={handleReset}>
              <Button size="small" danger icon={<RollbackOutlined />} loading={resetting} disabled={saving || loading}>恢复默认</Button>
            </Popconfirm>
            {(error || saveError) && <span className="sc-editor-hint bad" role="alert">{error ?? saveError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}
